"use client";

import Image from "next/image";
import { Check, Link2, X } from "lucide-react";
import { useState } from "react";

import { CcFortnoxVideo } from "@/components/services/ai-automation/fortnox/CcFortnoxVideo";
import { CcButton } from "@/components/ui/CcButton";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { GeoSubpageCopy } from "./geo-page-copy";

type CcGeoBeforeAfterProps = {
  copy: GeoSubpageCopy["beforeAfter"];
  aiCheckHref?: string;
};

/**
 * Interactive before/after (syncrun): the same question answered by an AI
 * service before and after GEO work, switchable between engines, beside a
 * real clip of someone asking on their phone. Leads to the free AI check.
 */
export function CcGeoBeforeAfter({ copy, aiCheckHref }: CcGeoBeforeAfterProps) {
  const [after, setAfter] = useState(true);
  const [engine, setEngine] = useState(0);
  const state = after ? copy.after : copy.before;
  const active = copy.engines[engine];

  return (
    <section
      className="cc-section cc-geo-ba"
      id="fore-och-efter-geo"
      aria-labelledby="cc-geo-ba-title"
      data-cc-nav="light"
      {...devSectionAttr("geo-before-after")}
    >
      <div className="cc-geo-ba__container">
        <header className="cc-geo-head">
          <p className="cc-geo-mono">{copy.eyebrow}</p>
          <h2 id="cc-geo-ba-title" className="cc-geo-title">
            {copy.title}
          </h2>
          <p className="cc-geo-intro">{copy.text}</p>
        </header>

        <div className="cc-geo-ba__grid">
          <div className="cc-geo-ba__media" aria-hidden="true">
            <CcFortnoxVideo
              src={copy.video.src}
              srcSmall={copy.video.srcSmall}
              poster={copy.video.poster}
              className="cc-geo-ba__video"
              sizes="(min-width: 1100px) 520px, 100vw"
            />
          </div>

          <div className="cc-geo-ba__panel">
            <div className="cc-geo-ba__controls">
              <div
                className="cc-geo-ba__toggle"
                role="group"
                aria-label={copy.title}
              >
                {copy.toggle.map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={after === (i === 1)}
                    onClick={() => setAfter(i === 1)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="cc-geo-ba__engines" role="group" aria-label="AI">
                {copy.engines.map((item, i) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={engine === i}
                    aria-label={item.name}
                    onClick={() => setEngine(i)}
                  >
                    <Image
                      src={item.logo}
                      alt=""
                      width={16}
                      height={16}
                      unoptimized
                    />
                    <span>{item.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="cc-geo-ba__chat" key={`${after}-${engine}`}>
              <p className="cc-geo-ba__q">{copy.question}</p>
              <div className="cc-geo-ba__a">
                <span className="cc-geo-ba__avatar">
                  <Image
                    src={active.logo}
                    alt=""
                    width={16}
                    height={16}
                    unoptimized
                  />
                </span>
                <div>
                  <p>{state.intro}</p>
                  <ol>
                    {state.items.map((item, i) => {
                      const isBrand = item.startsWith(copy.brand);
                      return (
                        <li
                          key={item}
                          style={{ ["--i" as string]: i }}
                          data-brand={isBrand ? "true" : undefined}
                        >
                          {isBrand ? (
                            <>
                              <mark>{copy.brand}</mark>
                              {item.slice(copy.brand.length)}
                            </>
                          ) : (
                            item
                          )}
                        </li>
                      );
                    })}
                  </ol>
                  {after ? (
                    <span className="cc-geo-ba__source">
                      <Link2 size={13} strokeWidth={2.2} />
                      {copy.after.source}
                      <Check size={13} strokeWidth={2.6} />
                    </span>
                  ) : (
                    <span className="cc-geo-ba__missing">
                      <X size={13} strokeWidth={2.6} />
                      {copy.before.missing}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="cc-geo-ba__cta">
              {aiCheckHref && <CcButton
                href={aiCheckHref}
                label={copy.cta}
                expand
                expandCompact
              />}
              <small>{copy.exampleNote}</small>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
